'use client'

import { getAnimationClass } from '@/components/common/toast/utils'
import { ImageCM } from '@/components/extension/image-cm'
import { Button } from '@/components/ui/button'
import { TagThumbnail } from '@/components/ui/tag-thumbnail'
import { formatPrice } from '@/lib/utils'
import { ArrowRight, X } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { toast as toastLib } from 'sonner'

export type ToastAddToCartData = {
  productName: string
  variantName: string
  regularPrice: number
  comparePrice: number
  imageUrl: string
}

export type ToastAddToCartSuccessParams = {
  position?:
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right'
  duration?: number
  title?: string
  ctaButton?: CTAButton
  tag?: {
    title: string
    icon?: React.ReactNode
  }
} & ToastAddToCartData

type CTAButton = {
  title: string
  href: string
}

export const showToastAddToCartSuccess = ({
  position = 'top-right',
  duration = 3000,
  tag,
  productName,
  variantName,
  regularPrice,
  comparePrice,
  imageUrl,
}: ToastAddToCartSuccessParams) => {
  return toastLib.custom(
    (id) => (
      <div className='relative w-full items-start gap-2 px-6 py-4 md:pr-7'>
        <button
          aria-controls='close button'
          className='absolute right-1 top-1 flex h-6 w-6 items-center justify-center text-neutral-900 hover:text-neutral-600'
          onClick={() => toastLib.dismiss(id)}
        >
          <X size={16} />
        </button>

        <p className='mb-1 border-b border-neutral-200 pb-3 font-criteria text-base font-semibold text-neutral-900'>
          <ToastCartTitle />
        </p>

        <div className='flex items-stretch justify-start gap-2'>
          {imageUrl && (
            <div className='h-auto w-16 flex-shrink-0'>
              <ImageCM
                src={imageUrl}
                width={120}
                height={160}
                useMediaUrl
                className='h-full w-full rounded-lg object-cover'
              />
            </div>
          )}
          <div className='flex-1'>
            {tag && (
              <TagThumbnail
                title={tag?.title}
                icon={tag?.icon}
                className='mb-1 bg-secondary-500 font-normal normal-case'
                variant='secondary'
                size='small'
              />
            )}

            <p className='font-sans text-base text-neutral-900'>
              {productName}
            </p>
            <p className='my-0.5 font-sans text-sm text-neutral-500'>
              {variantName}
            </p>
            <p className='font-sans text-base text-neutral-500'>
              <ins className='mr-2 font-semibold text-neutral-900 no-underline'>
                {formatPrice(regularPrice ?? 0)}
              </ins>
              {comparePrice && comparePrice !== regularPrice ? (
                <del className='font-thin text-neutral-400'>
                  {formatPrice(comparePrice)}
                </del>
              ) : null}
            </p>
          </div>
        </div>
        <div className='mt-6'>
          <CTAButton id={id as string} />
        </div>
      </div>
    ),
    {
      duration,
      position,
      unstyled: true,
      className: `px-0 bg-light w-fit h-fit shadow-[0px_1px_4px_0px_hsla(0,0%,0%,0.06),_0px_0px_24px_0px_hsla(0,0%,0%,0.06)] overflow-hidden rounded-2xl border border-neutral-200 border-l-0 ${getAnimationClass(position)} ${getAnimationClass(position)}`,
      cancel: true,
    }
  )
}

const ToastCartTitle = ({ title }: { title?: string }) => {
  const t = useTranslations('toast_cart')
  return <>{title || t('success_title')}</>
}

const CTAButton = ({ id }: { id: string }) => {
  const router = useRouter()
  const t = useTranslations('toast_cart')
  return (
    <Button
      size='default'
      variant='outline-neutral'
      className='mr-1'
      onClick={() => {
        router.push('/cart')
        toastLib.dismiss(id)
      }}
    >
      {t('view_cart')}
      <ArrowRight size={16} />
    </Button>
  )
}
